import { createRouter, createWebHashHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import type { App } from 'vue'
import { Layout, getParentLayout } from '@/utils/routerHelper'
import { useI18n } from '@/hooks/web/useI18n'
import { NO_RESET_WHITE_LIST } from '@/constants'
import { useUserStore } from '@/store/modules/user'
import { usePermissionStore } from '@/store/modules/permission'

const { t } = useI18n()

export const constantRouterMap: AppRouteRecordRaw[] = [
  {
    path: '/',
    component: Layout,
    redirect: '/',
    name: 'Root',
    meta: {
      hidden: true
    },
    children: [
      {
        path: '',
        component: () => import('@/views/Home.vue'),
        name: 'Home',
        meta: {
          hidden: true,
          noTagsView: true
        }
      }
    ]
  },
  {
    path: '/redirect',
    component: Layout,
    name: 'RedirectWrap',
    children: [
      {
        path: '/redirect/:path(.*)',
        name: 'Redirect',
        component: () => import('@/views/Redirect/Redirect.vue'),
        meta: {}
      }
    ],
    meta: {
      hidden: true,
      noTagsView: true
    }
  },
  {
    path: '/login',
    component: () => import('@/views/Login/Login.vue'),
    name: 'Login',
    meta: {
      hidden: true,
      title: t('router.login'),
      noTagsView: true
    }
  },
  {
    path: '/personal',
    component: Layout,
    redirect: '/personal/personal-center',
    name: 'Personal',
    meta: {
      title: t('router.personal'),
      hidden: true,
      canTo: true
    },
    children: [
      {
        path: 'personal-center',
        component: () => import('@/views/Personal/PersonalCenter/PersonalCenter.vue'),
        name: 'PersonalCenter',
        meta: {
          title: t('router.personalCenter'),
          hidden: true,
          canTo: true
        }
      }
    ]
  },
  {
    path: '/404',
    component: () => import('@/views/Error/404.vue'),
    name: 'NoFind',
    meta: {
      hidden: true,
      title: '404',
      noTagsView: true
    }
  }
]

export const asyncRouterMap: AppRouteRecordRaw[] = [
  {
    path: '/statistics',
    component: Layout,
    name: 'Statistics',
    redirect: '/statistics/index',
    meta: {
      title: '数据面板',
      icon: 'carbon:analytics'
    },
    children: [
      {
        path: 'index',
        name: 'StatisticsIndex',
        component: () => import('@/views/Statistics/Index.vue'),
        meta: {
          icon: 'carbon:analytics',
          title: '数据面板'
        }
      }
    ]
  },
  {
    path: '/statement',
    component: Layout,
    name: 'Statement',
    redirect: '/statement/profit',
    meta: {
      title: '数据报表',
      icon: 'carbon:report'
    },
    children: [
      {
        path: 'profit',
        name: 'StatementProfit',
        component: () => import('@/views/Statement/Profit.vue'),
        meta: {
          icon: 'carbon:report',
          title: '用户输赢报表'
        }
      }
    ]
  },
  {
    path: '/channel',
    component: Layout,
    name: 'Channel',
    redirect: '/channel/statistics',
    meta: {
      title: '渠道管理',
      icon: 'carbon:network-4'
    },
    children: [
      {
        path: 'statistics',
        name: 'ChannelStatistics',
        component: () => import('@/views/Channel/Statistics/Index.vue'),
        meta: {
          title: '统计数据'
        }
      },
      {
        path: 'statistics/detail',
        name: 'ChannelStatisticsDetail',
        component: () => import('@/views/Channel/Statistics/Detail.vue'),
        meta: {
          title: '数据统计详情'
        }
      }
    ]
  },
  {
    path: '/user',
    component: Layout,
    name: 'User',
    redirect: '/user/list',
    meta: {
      title: '用户管理',
      icon: 'ic:outline-manage-accounts'
    },
    children: [
      {
        path: 'list',
        name: 'UserList',
        component: () => import('@/views/User/List/List.vue'),
        meta: {
          icon: 'ic:outline-manage-accounts',
          title: '用户列表'
        }
      },
      {
        path: 'detail',
        name: 'UserDetail',
        component: () => import('@/views/User/List/Detail.vue'),
        meta: {
          hidden: true,
          activeMenu: '/user/list',
          title: '用户详情'
        }
      }
    ]
  },
  {
    path: '/financial',
    component: Layout,
    name: 'Financial',
    redirect: '/financial/recharge',
    meta: {
      title: '财务管理',
      icon: 'carbon:finance'
    },
    children: [
      {
        path: 'recharge',
        name: 'FinancialRecharge',
        component: () => import('@/views/Financial/Recharge.vue'),
        meta: {
          title: '充值管理'
        }
      },
      {
        path: 'withdraw',
        name: 'FinancialWithdraw',
        component: () => import('@/views/Financial/Withdraw.vue'),
        meta: {
          title: '提现管理'
        }
      }
    ]
  }
]

const router = createRouter({
  history: createWebHashHistory(),
  strict: true,
  routes: constantRouterMap as RouteRecordRaw[],
  scrollBehavior: () => ({ left: 0, top: 0 })
})

export const resetRouter = (): void => {
  router.getRoutes().forEach((route) => {
    const { name } = route
    if (name && !NO_RESET_WHITE_LIST.includes(name as string)) {
      router.hasRoute(name) && router.removeRoute(name)
    }
  })
}

export const setupRouter = (app: App<Element>) => {
  app.use(router)
}

router.beforeEach(async (to, from, next) => {
  const userStore = useUserStore()
  const permissionStore = usePermissionStore()

  // 如果没有token，跳转到登录页
  if (!userStore.getToken) {
    if (to.path === '/login') {
      next()
    } else {
      next('/login')
    }
    return
  }

  // 如果是登录页且已有token，跳转到首页
  if (to.path === '/login') {
    next()
    return
  }

  if (permissionStore.getIsAddRouters) {
    next()
    return
  }

  if (userStore.getToken) {
    await permissionStore.generateRoutes('static').catch(() => {})
    permissionStore.getAddRouters.forEach((route) => {
      router.addRoute(route as RouteRecordRaw) // 动态添加可访问路由表
    })
    permissionStore.setIsAddRouters(true)
    next({ ...to, replace: true })
  }
})

export default router
